import React, { useEffect, useState } from "react";
import type { OralCavity, Hair, Nails } from "../../../types/nurseFormTypes";
import api from "../../../api/axiosConfig";
import { useS3Uploader } from "../../../hooks/useS3Uploader";

// ⭐ ADD THIS IMPORT
import PreviewBox from "../Reuse/PreviewBox";

import "../../../center admin css/PhysicalExaminationForm.css";
import "../../../center admin css/centerAdmin.css";

interface FormComponentProps {
  emrId: string;
  onComplete?: () => void;
  onSymptomsObservedChange?: (value: boolean) => void;
}

type SkinAreaKey = "face" | "upperLimb" | "lowerLimb" | "trunk";

type SkinLesion = {
  elevation?: "raised" | "flat";
  size?: ">10mm" | "<10mm";

  colour?: "red" | "blue" | "black" | "purple" | "white" | "hypopigmented";

  fluidFilled?: boolean;
  clearFluid?: boolean;
  scaly?: boolean;
  blanching?: boolean;
  epidermalBreakage?: boolean;
  itchy?: boolean;
};

type SkinArea = {
  selected: boolean;
  findings: string[];
  onsetDate: string;
  imageUrls: string[];
  lesion: SkinLesion;
};

type SkinExam = {
  symptomsObserved: boolean;
  face: SkinArea;
  upperLimb: SkinArea;
  lowerLimb: SkinArea;
  trunk: SkinArea;
};

export const PhysicalExaminationForm: React.FC<FormComponentProps> = ({
  emrId,
  onComplete,
  onSymptomsObservedChange,
}) => {
  const { uploadImage } = useS3Uploader(emrId);

  const INITIAL_ORAL: OralCavity = {
    observed: "",
    buccalCavity: false,
    tongue: false,
    palate: false,
    lips: false,
    onsetDate: "",
    photos: [],
  };

  const INITIAL_HAIR: Hair = {
    observed: "",
    sparsity: false,
    flakingScalp: false,
    hairColourLightening: false,
    onsetDate: "",
    photos: [],
  };

  const INITIAL_NAILS: Nails = {
    observed: "",
    brittleness: false,
    swellingAroundNails: false,
    pigmentation: false,
    onsetDate: "",
    photos: [],
  };

  const INITIAL_SKIN: SkinExam = {
    symptomsObserved: false,

    face: {
      selected: false,
      findings: [],
      onsetDate: "",
      imageUrls: [],
      lesion: {},
    },

    upperLimb: {
      selected: false,
      findings: [],
      onsetDate: "",
      imageUrls: [],
      lesion: {},
    },

    lowerLimb: {
      selected: false,
      findings: [],
      onsetDate: "",
      imageUrls: [],
      lesion: {},
    },

    trunk: {
      selected: false,
      findings: [],
      onsetDate: "",
      imageUrls: [],
      lesion: {},
    },
  };

  // const INITIAL_SKIN: Cutaneous = {
  //   observed: "",
  //   location: {
  //     face: false,
  //     trunk: false,
  //     lowerLimbs: false,
  //     upperLimbs: false,
  //     others: false,
  //     othersText: "",
  //   },

  //   characteristics: {
  //     elevation: false,
  //     size: false,
  //     colour: false,
  //     fluidFilled: false,
  //     clearFluid: false,
  //     isScaly: false,
  //     doesItBlanch: false,
  //     epidermalBreakage: false,
  //     itchy: false,
  //   },

  //   onsetDate: "",
  //   photos: [],
  // };

  /* ---------------------------------------------------------
      STATE
  ---------------------------------------------------------- */

  const [oral, setOral] = useState<OralCavity>(INITIAL_ORAL);

  const [hair, setHair] = useState<Hair>(INITIAL_HAIR);

  const [nails, setNails] = useState<Nails>(INITIAL_NAILS);

  const [skin, setSkin] = useState<SkinExam>(INITIAL_SKIN);

  const [loading, setLoading] = useState(false);

  /* ---------------------------------------------------------
      LOAD EXISTING PHYSICAL EXAMINATION
  ---------------------------------------------------------- */

  const loadExistingPhysicalExam = async () => {
    if (!emrId) return;

    try {
      // Reset previous patient's data immediately
      setOral(INITIAL_ORAL);
      setHair(INITIAL_HAIR);
      setNails(INITIAL_NAILS);
      setSkin(INITIAL_SKIN);

      onSymptomsObservedChange?.(false);

      const res = await api.dataCollectionApi.get(
        `/api/forms/physical-exam/${encodeURIComponent(emrId)}`,
      );

      const saved = res.data?.data;

      if (!saved) {
        return;
      }

      const savedOral = saved.oralCavity;
      const savedHair = saved.hair;
      const savedNails = saved.nails;
      const savedSkin = saved.skinExam;

      const oralData: OralCavity = {
        ...INITIAL_ORAL,
        observed: savedOral?.symptomsObserved ? "yes" : "no",
        buccalCavity: savedOral?.findings?.includes("Buccal Cavity") ?? false,
        tongue: savedOral?.findings?.includes("Tongue") ?? false,
        palate: savedOral?.findings?.includes("Palate") ?? false,
        lips: savedOral?.findings?.includes("Lips") ?? false,
        onsetDate: savedOral?.onsetDate ? savedOral.onsetDate.slice(0, 10) : "",
        photos: savedOral?.imageUrls ?? [],
      };

      const hairData: Hair = {
        ...INITIAL_HAIR,
        observed: savedHair?.symptomsObserved ? "yes" : "no",
        sparsity: savedHair?.findings?.includes("Sparsity") ?? false,
        flakingScalp:
          savedHair?.findings?.includes("Flaking of Scalp") ?? false,
        hairColourLightening:
          savedHair?.findings?.includes("Hair Colour Lightening") ?? false,
        onsetDate: savedHair?.onsetDate ? savedHair.onsetDate.slice(0, 10) : "",
        photos: savedHair?.imageUrls ?? [],
      };

      const nailsData: Nails = {
        ...INITIAL_NAILS,
        observed: savedNails?.symptomsObserved ? "yes" : "no",
        brittleness: savedNails?.findings?.includes("Brittleness") ?? false,
        swellingAroundNails:
          savedNails?.findings?.includes("Swelling Around Nails") ?? false,
        pigmentation: savedNails?.findings?.includes("Pigmentation") ?? false,
        onsetDate: savedNails?.onsetDate
          ? savedNails.onsetDate.slice(0, 10)
          : "",
        photos: savedNails?.imageUrls ?? [],
      };

      // const skinLocations = String(savedSkin?.location ?? "")
      //   .split(",")
      //   .map((value: string) => value.trim())
      //   .filter(Boolean);

      // const skinData: Cutaneous = {
      //   ...INITIAL_SKIN,
      //   observed: savedSkin?.symptomsObserved ? "yes" : "no",
      //   location: {
      //     ...INITIAL_SKIN.location,
      //     face: skinLocations.includes("Face"),
      //     trunk: skinLocations.includes("Trunk"),
      //     lowerLimbs: skinLocations.includes("Lower Limbs"),
      //     upperLimbs: skinLocations.includes("Upper Limbs"),
      //     others: skinLocations.includes("Others"),
      //   },
      //   onsetDate: savedSkin?.onsetDate ? savedSkin.onsetDate.slice(0, 10) : "",
      //   photos: savedSkin?.imageUrls ?? [],
      // };

      const createSkinArea = (savedArea: any): SkinArea => ({
        selected: savedArea?.selected ?? false,
        findings: savedArea?.findings ?? [],
        onsetDate: savedArea?.onsetDate ? savedArea.onsetDate.slice(0, 10) : "",
        imageUrls: savedArea?.imageUrls ?? [],
        lesion: savedArea?.lesion ?? {},
      });

      const skinData: SkinExam = {
        symptomsObserved: savedSkin?.symptomsObserved ?? false,

        face: createSkinArea(savedSkin?.face),

        upperLimb: createSkinArea(savedSkin?.upperLimb),

        lowerLimb: createSkinArea(savedSkin?.lowerLimb),

        trunk: createSkinArea(savedSkin?.trunk),
      };

      setOral(oralData);
      setHair(hairData);
      setNails(nailsData);
      setSkin(skinData);

      const symptomsObserved =
        oralData.observed === "yes" ||
        hairData.observed === "yes" ||
        nailsData.observed === "yes" ||
        skinData.symptomsObserved;

      onSymptomsObservedChange?.(symptomsObserved);
    } catch (err: any) {
      // No existing record is okay.
      if (err?.response?.status !== 404) {
        console.error("Failed to load existing Physical Examination:", err);
      }
    }
  };

  useEffect(() => {
    loadExistingPhysicalExam();
  }, [emrId]);

  /* ---------------------------------------------------------
      FILE UPLOAD HANDLER
  ---------------------------------------------------------- */
  const handleUpload =
    (cb: (urls: string[]) => void, moduleName: string) =>
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files ? Array.from(e.target.files) : [];
      if (!files.length) return;

      let uploadedUrls: string[] = [];

      for (const file of files) {
        const result = await uploadImage(file, moduleName);

        const imageUrl = result?.previewUrl ?? result?.viewUrl;

        if (imageUrl) {
          uploadedUrls.push(imageUrl);
        }
      }

      cb(uploadedUrls);
    };

  /* ---------------------------------------------------------
      SUBMIT
  ---------------------------------------------------------- */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!oral.observed || !hair.observed || !nails.observed) {
      alert("Please select Yes or No for all sections.");
      return;
    }

    // ✅ only after successful validation
    // onComplete && onComplete();
    const symptomsObserved =
      oral.observed === "yes" ||
      hair.observed === "yes" ||
      nails.observed === "yes" ||
      skin.symptomsObserved;

    // ✅ only after successful validation
    onSymptomsObservedChange?.(symptomsObserved);
    onComplete?.();

    const payload = {
      emrId,

      oralCavity: {
        symptomsObserved: oral.observed === "yes",

        findings:
          oral.observed === "yes"
            ? [
                oral.buccalCavity && "Buccal Cavity",
                oral.tongue && "Tongue",
                oral.palate && "Palate",
                oral.lips && "Lips",
              ].filter(Boolean)
            : [],

        ...(oral.observed === "yes" &&
          oral.onsetDate && {
            onsetDate: new Date(oral.onsetDate).toISOString(),
          }),

        imageUrls: oral.observed === "yes" ? oral.photos : [],
      },

      hair: {
        symptomsObserved: hair.observed === "yes",

        findings:
          hair.observed === "yes"
            ? [
                hair.sparsity && "Sparsity",
                hair.flakingScalp && "Flaking of Scalp",
                hair.hairColourLightening && "Hair Colour Lightening",
              ].filter(Boolean)
            : [],

        ...(hair.observed === "yes" &&
          hair.onsetDate && {
            onsetDate: new Date(hair.onsetDate).toISOString(),
          }),

        imageUrls: hair.observed === "yes" ? hair.photos : [],
      },

      nails: {
        symptomsObserved: nails.observed === "yes",

        findings:
          nails.observed === "yes"
            ? [
                nails.brittleness && "Brittleness",
                nails.swellingAroundNails && "Swelling Around Nails",
                nails.pigmentation && "Pigmentation",
              ].filter(Boolean)
            : [],

        ...(nails.observed === "yes" &&
          nails.onsetDate && {
            onsetDate: new Date(nails.onsetDate).toISOString(),
          }),

        imageUrls: nails.observed === "yes" ? nails.photos : [],
      },

      skinExam: {
        symptomsObserved: skin.symptomsObserved,

        face: {
          selected: skin.face.selected,
          findings: skin.face.findings,

          ...(skin.face.onsetDate && {
            onsetDate: new Date(skin.face.onsetDate).toISOString(),
          }),

          imageUrls: skin.face.imageUrls,

          lesion: skin.face.lesion,
        },

        upperLimb: {
          selected: skin.upperLimb.selected,
          findings: skin.upperLimb.findings,

          ...(skin.upperLimb.onsetDate && {
            onsetDate: new Date(skin.upperLimb.onsetDate).toISOString(),
          }),

          imageUrls: skin.upperLimb.imageUrls,

          lesion: skin.upperLimb.lesion,
        },

        lowerLimb: {
          selected: skin.lowerLimb.selected,
          findings: skin.lowerLimb.findings,

          ...(skin.lowerLimb.onsetDate && {
            onsetDate: new Date(skin.lowerLimb.onsetDate).toISOString(),
          }),

          imageUrls: skin.lowerLimb.imageUrls,

          lesion: skin.lowerLimb.lesion,
        },

        trunk: {
          selected: skin.trunk.selected,
          findings: skin.trunk.findings,

          ...(skin.trunk.onsetDate && {
            onsetDate: new Date(skin.trunk.onsetDate).toISOString(),
          }),

          imageUrls: skin.trunk.imageUrls,

          lesion: skin.trunk.lesion,
        },
      },
    };

    try {
      setLoading(true);
      const res = await api.dataCollectionApi.post(
        "/api/forms/physical-exam",
        payload,
      );

      if (res.data.success) {
        alert("Physical Examination submitted successfully!");

        await loadExistingPhysicalExam();
      } else {
        alert("Submission failed");
      }
    } catch (err) {
      console.error(err);
      alert("Error submitting form");
    } finally {
      setLoading(false);
    }
  };

  const handleSkinUpload =
    (region: SkinAreaKey) => async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files ? Array.from(e.target.files) : [];

      if (!files.length) return;

      const moduleMap: Record<SkinAreaKey, string> = {
        face: "physical-exam/skin/face",
        upperLimb: "physical-exam/skin/upper-limb",
        lowerLimb: "physical-exam/skin/lower-limb",
        trunk: "physical-exam/skin/trunk",
      };

      let uploadedUrls: string[] = [];

      for (const file of files) {
        const result = await uploadImage(file, moduleMap[region]);

        const imageUrl = result?.previewUrl ?? result?.viewUrl;

        if (imageUrl) {
          uploadedUrls.push(imageUrl);
        }
      }

      if (uploadedUrls.length === 0) return;

      setSkin((prev) => ({
        ...prev,
        [region]: {
          ...prev[region],
          imageUrls: [...prev[region].imageUrls, ...uploadedUrls],
        },
      }));
    };

  const skinRegions: {
    key: SkinAreaKey;
    label: string;
  }[] = [
    { key: "face", label: "Face" },
    { key: "upperLimb", label: "Upper Limb" },
    { key: "lowerLimb", label: "Lower Limb" },
    { key: "trunk", label: "Trunk" },
  ];

  // const skinFindingOptions = [
  //   "Elevationeeeeeeeeeeeee",
  //   "Size",
  //   "Colour",
  //   "Fluid-Filled",
  //   "Clear Fluid",
  //   "Scaly",
  //   "Blanching",
  //   "Epidermal Breakage",
  //   "Itchy",
  // ];

  const updateSkinRegion = (
    region: SkinAreaKey,
    updates: Partial<SkinArea>,
  ) => {
    setSkin((prev) => ({
      ...prev,
      [region]: {
        ...prev[region],
        ...updates,
      },
    }));
  };

  const toggleSkinFinding = (region: SkinAreaKey, finding: string) => {
    setSkin((prev) => {
      const currentFindings = prev[region].findings;

      const findings = currentFindings.includes(finding)
        ? currentFindings.filter((item) => item !== finding)
        : [...currentFindings, finding];

      return {
        ...prev,
        [region]: {
          ...prev[region],
          findings,
        },
      };
    });
  };

  const removeSkinImage = (region: SkinAreaKey, index: number) => {
    setSkin((prev) => ({
      ...prev,
      [region]: {
        ...prev[region],
        imageUrls: prev[region].imageUrls.filter((_, i) => i !== index),
      },
    }));
  };

  /* ---------------------------------------------------------
      UI
  ---------------------------------------------------------- */
  return (
    <form className="pe-form" onSubmit={handleSubmit}>
      <div className="vs-section-bar">Physical Examination Findings</div>

      {/* ===================== ORAL ===================== */}
      <div className="pe-card">
        <div className="pe-card-title">Oral Cavity</div>

        {/* Observed */}
        <div className="pe-radio">
          <label>Symptoms Observed?</label>
          <div>
            <label>
              <input
                type="radio"
                checked={oral.observed === "yes"}
                onChange={() => setOral({ ...oral, observed: "yes" })}
              />
              Yes
            </label>
            <label>
              <input
                type="radio"
                checked={oral.observed === "no"}
                onChange={() => setOral({ ...oral, observed: "no" })}
              />
              No
            </label>
          </div>
        </div>

        {oral.observed === "yes" && (
          <>
            {/* Checkboxes */}
            <div className="pe-checkboxes">
              {[
                ["buccalCavity", "Buccal Cavity"],
                ["tongue", "Tongue"],
                ["palate", "Palate"],
                ["lips", "Lips"],
              ].map(([key, label]) => (
                <label key={key}>
                  <input
                    type="checkbox"
                    checked={(oral as any)[key]}
                    onChange={(e) =>
                      setOral({ ...oral, [key]: e.target.checked })
                    }
                  />
                  {label}
                </label>
              ))}
            </div>

            {/* Date */}
            <div className="pe-onset">
              <label>Date of Onset</label>
              <input
                type="date"
                value={oral.onsetDate}
                onChange={(e) =>
                  setOral({ ...oral, onsetDate: e.target.value })
                }
              />
            </div>

            {/* Upload */}
            <div className="pe-upload">
              <label className="pe-upload-btn">
                Upload Images
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  hidden
                  onChange={handleUpload(
                    (urls) =>
                      setOral({ ...oral, photos: [...oral.photos, ...urls] }),
                    "physical-exam/oral",
                  )}
                />
              </label>
            </div>

            {/* ⭐ USES EXTERNAL PREVIEW BOX */}

            <PreviewBox
              urls={oral.photos}
              onRemove={(index) =>
                setOral({
                  ...oral,
                  photos: oral.photos.filter((_, i) => i !== index),
                })
              }
            />
          </>
        )}
      </div>

      {/* ===================== HAIR ===================== */}
      <div className="pe-card">
        <div className="pe-card-title">Hair</div>

        <div className="pe-radio">
          <label>Symptoms Observed?</label>
          <div>
            <label>
              <input
                type="radio"
                checked={hair.observed === "yes"}
                onChange={() => setHair({ ...hair, observed: "yes" })}
              />
              Yes
            </label>
            <label>
              <input
                type="radio"
                checked={hair.observed === "no"}
                onChange={() => setHair({ ...hair, observed: "no" })}
              />
              No
            </label>
          </div>
        </div>
        {hair.observed === "yes" && (
          <>
            <div className="pe-checkboxes">
              {[
                ["sparsity", "Sparsity"],
                ["flakingScalp", "Flaking Scalp"],
                ["hairColourLightening", "Hair Colour Lightening"],
              ].map(([key, label]) => (
                <label key={key}>
                  <input
                    type="checkbox"
                    checked={(hair as any)[key]}
                    onChange={(e) =>
                      setHair({ ...hair, [key]: e.target.checked })
                    }
                  />
                  {label}
                </label>
              ))}
            </div>

            <div className="pe-onset">
              <label>Date of Onset</label>
              <input
                type="date"
                value={hair.onsetDate}
                onChange={(e) =>
                  setHair({ ...hair, onsetDate: e.target.value })
                }
              />
            </div>

            <div className="pe-upload">
              <label className="pe-upload-btn">
                Upload Images
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  hidden
                  onChange={handleUpload(
                    (urls) =>
                      setHair({ ...hair, photos: [...hair.photos, ...urls] }),
                    "physical-exam/hair",
                  )}
                />
              </label>
            </div>

            <PreviewBox
              urls={hair.photos}
              onRemove={(index) =>
                setHair({
                  ...hair,
                  photos: hair.photos.filter((_, i) => i !== index),
                })
              }
            />
          </>
        )}
      </div>

      {/* ===================== NAILS ===================== */}
      <div className="pe-card">
        <div className="pe-card-title">Nails</div>

        <div className="pe-radio">
          <label>Symptoms Observed?</label>
          <div>
            <label>
              <input
                type="radio"
                checked={nails.observed === "yes"}
                onChange={() => setNails({ ...nails, observed: "yes" })}
              />
              Yes
            </label>
            <label>
              <input
                type="radio"
                checked={nails.observed === "no"}
                onChange={() => setNails({ ...nails, observed: "no" })}
              />
              No
            </label>
          </div>
        </div>

        {nails.observed === "yes" && (
          <>
            <div className="pe-checkboxes">
              {[
                ["brittleness", "Brittleness"],
                ["swellingAroundNails", "Swelling Around Nails"],
                ["pigmentation", "Pigmentation"],
              ].map(([key, label]) => (
                <label key={key}>
                  <input
                    type="checkbox"
                    checked={(nails as any)[key]}
                    onChange={(e) =>
                      setNails({ ...nails, [key]: e.target.checked })
                    }
                  />
                  {label}
                </label>
              ))}
            </div>

            <div className="pe-onset">
              <label>Date of Onset</label>
              <input
                type="date"
                value={nails.onsetDate}
                onChange={(e) =>
                  setNails({ ...nails, onsetDate: e.target.value })
                }
              />
            </div>

            <div className="pe-upload">
              <label className="pe-upload-btn">
                Upload Images
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  hidden
                  onChange={handleUpload(
                    (urls) =>
                      setNails({
                        ...nails,
                        photos: [...nails.photos, ...urls],
                      }),
                    "physical-exam/nails",
                  )}
                />
              </label>
            </div>

            {/* <PreviewBox urls={nails.photos} /> */}
            <PreviewBox
              urls={nails.photos}
              onRemove={(index) =>
                setNails({
                  ...nails,
                  photos: nails.photos.filter((_, i) => i !== index),
                })
              }
            />
          </>
        )}
      </div>

      {/* ===================== SKIN ===================== */}
      {/* ===================== SKIN ===================== */}
      <div className="pe-card">
        <div className="pe-card-title">Skin / Cutaneous Exam</div>

        {/* Symptoms Observed */}
        <div className="pe-radio">
          <label>Symptoms Observed?</label>

          <div>
            <label>
              <input
                type="radio"
                checked={skin.symptomsObserved === true}
                onChange={() =>
                  setSkin((prev) => ({
                    ...prev,
                    symptomsObserved: true,
                  }))
                }
              />
              Yes
            </label>

            <label>
              <input
                type="radio"
                checked={skin.symptomsObserved === false}
                onChange={() =>
                  setSkin((prev) => ({
                    ...prev,
                    symptomsObserved: false,
                    face: {
                      ...prev.face,
                      selected: false,
                    },
                    upperLimb: {
                      ...prev.upperLimb,
                      selected: false,
                    },
                    lowerLimb: {
                      ...prev.lowerLimb,
                      selected: false,
                    },
                    trunk: {
                      ...prev.trunk,
                      selected: false,
                    },
                  }))
                }
              />
              No
            </label>
          </div>
        </div>

        {skin.symptomsObserved && (
          <>
            {/* ================= REGIONS ================= */}
            <div className="pe-checkbox-grid">
              {skinRegions.map((region) => (
                <label key={region.key}>
                  <input
                    type="checkbox"
                    checked={skin[region.key].selected}
                    onChange={(e) =>
                      updateSkinRegion(region.key, {
                        selected: e.target.checked,
                      })
                    }
                  />

                  {region.label}
                </label>
              ))}
            </div>

            {/* ================= REGION DETAILS ================= */}
            {skinRegions.map((region) => {
              const area = skin[region.key];

              if (!area.selected) {
                return null;
              }

              return (
                <div key={region.key} className="pe-card pe-skin-region-card">
                  <div className="pe-card-title">{region.label}</div>

                  {/* Findings */}
                  {/* <div className="pe-checkbox-grid">
        {skinFindingOptions.map((finding) => (
          <label key={finding}>
            <input
              type="checkbox"
              checked={area.findings.includes(finding)}
              onChange={() =>
                toggleSkinFinding(region.key, finding)
              }
            />

            {finding}
          </label>
        ))}
      </div> */}

                  {/* Lesion Values */}
                  {/* Lesion Values */}
                  <div className="pe-skin-lesion-values">
                    <label>
                      <span className="pe-skin-field-label">Elevation</span>

                      <select
                        className="pe-skin-select"
                        value={area.lesion.elevation ?? ""}
                        onChange={(e) =>
                          updateSkinRegion(region.key, {
                            lesion: {
                              ...area.lesion,
                              elevation:
                                (e.target.value as "raised" | "flat" | "") ||
                                undefined,
                            },
                          })
                        }
                      >
                        <option value="">Select</option>
                        <option value="raised">Raised</option>
                        <option value="flat">Flat</option>
                      </select>
                    </label>

                    <label>
                      <span className="pe-skin-field-label">Size</span>

                      <select
                        className="pe-skin-select"
                        value={area.lesion.size ?? ""}
                        onChange={(e) =>
                          updateSkinRegion(region.key, {
                            lesion: {
                              ...area.lesion,
                              size:
                                (e.target.value as ">10mm" | "<10mm" | "") ||
                                undefined,
                            },
                          })
                        }
                      >
                        <option value="">Select</option>
                        <option value="<10mm">&lt;10mm</option>
                        <option value=">10mm">&gt;10mm</option>
                      </select>
                    </label>

                    <label>
                      <span className="pe-skin-field-label">Colour</span>

                      <select
                        className="pe-skin-select"
                        value={area.lesion.colour ?? ""}
                        onChange={(e) =>
                          updateSkinRegion(region.key, {
                            lesion: {
                              ...area.lesion,
                              colour:
                                (e.target.value as
                                  | "red"
                                  | "blue"
                                  | "black"
                                  | "purple"
                                  | "white"
                                  | "hypopigmented"
                                  | "") || undefined,
                            },
                          })
                        }
                      >
                        <option value="">Select</option>
                        <option value="red">Red</option>
                        <option value="blue">Blue</option>
                        <option value="black">Black</option>
                        <option value="purple">Purple</option>
                        <option value="white">White</option>
                        <option value="hypopigmented">Hypopigmented</option>
                      </select>
                    </label>
                  </div>

                  {/* Boolean lesion properties */}
                  <div className="pe-checkbox-grid">
                    {[
                      ["fluidFilled", "Fluid-Filled"],
                      ["clearFluid", "Clear Fluid"],
                      ["scaly", "Scaly"],
                      ["blanching", "Blanching"],
                      ["epidermalBreakage", "Epidermal Breakage"],
                      ["itchy", "Itchy"],
                    ].map(([key, label]) => (
                      <label key={key}>
                        <input
                          type="checkbox"
                          checked={!!area.lesion[key as keyof SkinLesion]}
                          onChange={(e) =>
                            updateSkinRegion(region.key, {
                              lesion: {
                                ...area.lesion,
                                [key]: e.target.checked,
                              },
                            })
                          }
                        />

                        {label}
                      </label>
                    ))}
                  </div>

                  {/* Date */}
                  <div className="pe-onset">
                    <label>Date of Onset</label>

                    <input
                      type="date"
                      value={area.onsetDate}
                      onChange={(e) =>
                        updateSkinRegion(region.key, {
                          onsetDate: e.target.value,
                        })
                      }
                    />
                  </div>

                  {/* Upload */}
                  <div className="pe-upload">
                    <label className="pe-upload-btn">
                      Upload Images
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        hidden
                        onChange={handleSkinUpload(region.key)}
                      />
                    </label>
                  </div>

                  <PreviewBox
                    urls={area.imageUrls}
                    onRemove={(index) => removeSkinImage(region.key, index)}
                  />
                </div>
              );
            })}
          </>
        )}
      </div>

      {/* ACTION BUTTONS */}
      <div className="audio-actions">
        <button type="button" className="audio-btn audio-btn-secondary">
          Close
        </button>

        <button
          type="submit"
          className="audio-btn audio-btn-primary"
          disabled={loading}
        >
          {loading ? "Submitting..." : "Submit Report"}
        </button>
      </div>
    </form>
  );
};

export default PhysicalExaminationForm;
